/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */

type VersionInfo = {
    version: string;
    time: number;
};

type VersionTrackerState = {
    initial: VersionInfo | undefined;
    firstUpdate: VersionInfo | undefined;
    latest: VersionInfo | undefined;
    lastNudgeShown: number | undefined;
};

const LAST_NUDGE_SHOWN_KEY = "version-tracker:lastNudgeShown";

const _globalState: VersionTrackerState = (() => {
    (globalThis as any).versionTrackerState = (globalThis as any).versionTrackerState || ({} as VersionTrackerState);
    return (globalThis as any).versionTrackerState;
})();

export function startVersionTracker(
    delayMs = 2 * 60 * 1000, // 2 minutes
    versionUrl = "/version",
    initialState = {} as VersionTrackerState,
) {
    _globalState.initial = initialState.initial;
    _globalState.firstUpdate = initialState.firstUpdate;
    _globalState.latest = initialState.latest;
    _globalState.lastNudgeShown = initialState.lastNudgeShown ?? loadLastNudgeShown();

    const _timer = setInterval(async () => {
        const fetchedVersion = await fetchVersion(versionUrl);
        if (!fetchedVersion) {
            return;
        }
        detectFirstVersionUpdate(fetchedVersion);
        setVersionState(fetchedVersion);
    }, delayMs);

    return () => {
        _globalState.initial = undefined;
        _globalState.firstUpdate = undefined;
        _globalState.latest = undefined;
        _globalState.lastNudgeShown = undefined;
        clearInterval(_timer);
    };
}

export function isFrontendUpdated(beforeTime: number = Date.now()): boolean {
    const timeOfFirstUpdate = _globalState.firstUpdate?.time;
    if (!timeOfFirstUpdate) {
        return false;
    }
    return timeOfFirstUpdate < beforeTime;
}

export function shouldShowUpdateNudge(): boolean {
    const timeOfFirstUpdate = _globalState.firstUpdate?.time;
    if (!timeOfFirstUpdate) {
        return false;
    }

    // Grace period: only show nudge if update was detected >1 hour ago
    const ONE_HOUR = 60 * 60 * 1000;
    const timeSinceUpdate = Date.now() - timeOfFirstUpdate;
    if (timeSinceUpdate < ONE_HOUR) {
        return false;
    }

    // Only show nudge once per day
    const lastNudgeShown = _globalState.lastNudgeShown;
    if (lastNudgeShown) {
        const ONE_DAY = 24 * 60 * 60 * 1000;
        const timeSinceLastNudge = Date.now() - lastNudgeShown;
        if (timeSinceLastNudge < ONE_DAY) {
            return false;
        }
    }

    return true;
}

export function markNudgeShown(): void {
    const now = Date.now();
    _globalState.lastNudgeShown = now;
    saveLastNudgeShown(now);
}

function detectFirstVersionUpdate(fetchedVersion: VersionInfo) {
    if (!_globalState.initial) {
        return; // nothing to compare to
    }
    if (_globalState.firstUpdate) {
        return; // first update was already detected
    }

    if (_globalState.initial.version !== fetchedVersion.version) {
        _globalState.firstUpdate = fetchedVersion;

        console.log("Detected frontend version change", { previous: _globalState.initial, current: fetchedVersion });
    }
}

function setVersionState(fetchedVersion: VersionInfo) {
    if (!_globalState.initial) {
        _globalState.initial = fetchedVersion;
    }
    _globalState.latest = fetchedVersion;
}

function loadLastNudgeShown(): number | undefined {
    try {
        const stored = localStorage.getItem(LAST_NUDGE_SHOWN_KEY);
        if (stored) {
            const parsed = Number(stored);
            if (!isNaN(parsed)) {
                return parsed;
            }
        }
    } catch {
        // localStorage may be unavailable (e.g. private browsing)
    }
    return undefined;
}

function saveLastNudgeShown(timestamp: number): void {
    try {
        localStorage.setItem(LAST_NUDGE_SHOWN_KEY, String(timestamp));
    } catch {
        // localStorage may be unavailable
    }
}

async function fetchVersion(versionUrl: string): Promise<VersionInfo | undefined> {
    let versionContent: string;
    try {
        const response = await fetch(versionUrl);
        if (!response.ok) {
            return; // silently ignore errors
        }
        versionContent = await response.text();
        const parsedVersion = JSON.parse(versionContent);
        if ("frontend" in parsedVersion) {
            versionContent = parsedVersion.frontend as string;
        }
    } catch {
        // silently ignore errors
        return;
    }
    return {
        version: versionContent,
        time: Date.now(),
    };
}
